import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError, getCatalogProducts, getProductBySlug, getRelatedProducts } from "@/lib/api-client";
import { ProductPurchaseArea } from "@/components/product/ProductPurchaseArea";
import { RelatedProductsRow } from "@/components/product/RelatedProductsRow";
import { LoadingBlock, ErrorBlock } from "@/components/product/QueryFeedback";
import { NotFoundPage } from "@/pages/shop/NotFoundPage";

/**
 * Équivalent de apps/web/app/(shop)/p/[slug]/page.tsx (fiche produit).
 *
 * Écarts assumés par rapport à l'original :
 *  - Les données structurées `<script type="application/ld+json">`
 *    (`product.seo.structuredData`) sont volontairement omises : SEO hors
 *    périmètre de cet atelier (cf. description de tâche, point 4).
 *  - `favoriteVariantIds`/`isLoggedIn` ne sont plus résolus ici puis transmis
 *    en props : `ProductPurchaseArea` les lit lui-même (`useCustomerAuth()` +
 *    `useQuery(["wishlist"])`), plus simple qu'un aller-retour côté page qui
 *    n'a plus de session serveur à lire (cf. commentaire de tête de ce
 *    composant).
 */
export function ProductDetailPage() {
  const { slug = "" } = useParams<{ slug: string }>();

  const productQuery = useQuery({
    queryKey: ["catalog", "product", slug],
    queryFn: () => getProductBySlug(slug),
    enabled: slug.length > 0,
  });

  const product = productQuery.data;

  // Bloc « Complétez votre guide » (§4.4, GUIDE_NEXT_STEP) : n'est pas inclus dans
  // `product.relations` (seuls upsell/crossSell le sont), d'où cette requête séparée.
  const guideNextStepQuery = useQuery({
    queryKey: ["catalog", "product", slug, "related", "GUIDE_NEXT_STEP"],
    queryFn: () => getRelatedProducts(slug, "GUIDE_NEXT_STEP"),
    enabled: Boolean(product),
  });

  const brandSlug = product?.brand?.slug;
  const sameBrandQuery = useQuery({
    queryKey: ["catalog", "products", "brand", brandSlug],
    queryFn: () => getCatalogProducts({ brand: brandSlug, limit: 9 }),
    enabled: Boolean(brandSlug),
  });

  if (productQuery.isLoading) return <LoadingBlock />;
  if (productQuery.error instanceof ApiError && productQuery.error.status === 404) return <NotFoundPage />;
  if (productQuery.isError || !product) return <ErrorBlock />;

  const guideNextStep = guideNextStepQuery.data ?? [];
  const sameBrandItems = (sameBrandQuery.data?.items ?? []).filter((item) => item.id !== product.id).slice(0, 8);

  return (
    <div className="container-vvs py-4">
      <nav aria-label="Fil d'Ariane" className="mb-4 flex flex-wrap gap-1 font-body text-xs text-muted">
        {product.breadcrumb.map((crumb) => (
          <span key={crumb.href} className="flex items-center gap-1">
            <Link to={crumb.href} className="hover:text-navy">
              {crumb.label}
            </Link>
            <span aria-hidden="true">/</span>
          </span>
        ))}
        <span className="text-ink">{product.name}</span>
      </nav>

      <ProductPurchaseArea product={product} />

      <RelatedProductsRow title="Souvent acheté avec" items={product.relations.crossSell} />
      <RelatedProductsRow title="Vous aimerez peut-être aussi" items={product.relations.upsell} />
      <RelatedProductsRow title="Complétez votre guide" items={guideNextStep} />
      {product.brand ? (
        <RelatedProductsRow title={`De la même marque : ${product.brand.name}`} items={sameBrandItems} />
      ) : null}
    </div>
  );
}
